import { appstr } from "../utils/appstr";
import { convertStdDateString } from "../utils/convertStdDateString";
import {
  enumDateTimeDisplayOptions,
  enumObjClassNames,
  enumObjectTypes,
  enumOverrideType,
  kEmployeePositionID,
  kPersonID,
  kPositionID,
  kPositionTitleID,
  kSupervisorID,
  kTitleID,
  objTypeClassMap
} from "../constants";
import { Globals } from "./Globals";
import { FormatEmployeeDictLabel } from "../utils/FormatEmployeeDictLabel";
import { formatArray } from "../utils/formatArray";
import { GetFlattenedChanges } from "../utils/GetFlattenedChanges";
import {
  FormatFieldValue,
  isBlockedField,
  getBlockedFieldDisplayValue,
  sanitizeBlockedValue
} from "../utils/FormatFieldValue";
import { FormatEmployeeLabelValue } from "../utils/FormatEmployeeLabelValue";
import { QXDoc } from "./QXDoc";
import { QObjDBObject } from "./QObjDBObject";

/**
 * @typedef {import('./QObjDBFieldDef').QObjDBFieldDef} QObjDBFieldDef
 * @typedef {import('./QObjDBObject').QObjDBObject} QObjDBObject
 * @typedef {import('./QPlanManager').QPlanManager} QPlanManager
 */
export class QChange {
  constructor() {
    this.fChangeType = null;
    this.fChangeAuthor = null;
    /**
     * @type {(null | QObjDBObject)}
     */
    this.fDBObj = null;
    /**
     * @type {(null | QPlanManager)}
     */
    this.fPlanMgr = null;
    /**
     * @type {(null | QObjDBObject)}
     */
    this.fParentOverride = null;
    /**
     * @type {(null | object)}
     */
    this.fDelta = null;
    /**
     * @type {(null | string)}
     */
    this.fActionID = null;
    /**
     * @type {(null | boolean)}
     */
    this.fConflict = null;
    /**
     * @type {(null | boolean)}
     */
    this.fIsReverted = null;
    /**
     * @type {(null | Date)}
     */
    this.fEffectiveDate = null;
    this.fAuthorBlockedFields = null;
    this.fOldRecVals = {};
  }

  InitChange(wsMgr, theChange, theOverride, dbObj, overrideClass) {
    this.fWSMgr = wsMgr;
    this.fDBObj = dbObj;
    var refObj = theOverride.SmartGetFieldValue("RefObj");
    if (dbObj == null && refObj != null && refObj instanceof QObjDBObject) this.fDBObj = refObj;
    this.fParentOverride = theOverride;
    this.fChangeType = theChange["type"] || theOverride.SmartGetFieldValue("Type");
    this.fChangeAuthor = theChange["author"];

    this.fChangeDate = theChange["date"];
    this.fDelta = theChange["data"] || {};
    this.fConflict = theChange["conflict"] == true;
    this.fConflictFields = theChange["conflictFields"];
    this.fAuthorBlockedFields = theChange["authorBlockedFields"]
      ? [...theChange["authorBlockedFields"]]
      : null;
    this.fIsReverted = theChange["revert"] == true;
    this.fActionID = theChange["actionID"] || null;

    if (theChange["effectiveDate"]) {
      var effDate = convertStdDateString(theChange["effectiveDate"]);
      // apply timezone offset to saved utc date
      var offset = effDate.getTimezoneOffset() / 60;
      var hours = 12;
      effDate.setHours(hours - offset);
      this.fEffectiveDate = effDate;
    }
    if (theChange["previous"]) this.fOldRecVals = theChange["previous"];
  }

  /**
   *
   * @param {object} changeDict
   * @param {string=} newType
   */
  UpdateChange(changeDict, newType = null) {
    const isPositionDocument = this.fWSMgr.GetDoc()?.IsPositionDocument();

    // Update delta of single change with changeDict of same format passed to InitChange
    var incomingChangeDelta = { ...changeDict["data"] } || {};
    if (newType === enumOverrideType.add) {
      const recordKey = this.fDBObj.GetRecordIDFieldDef().GetFieldName();
      const parentRecordKey = this.fDBObj.GetRecordParentIDFieldDef()?.GetFieldName();

      if (this.fDelta[recordKey] == null) {
        this.fDelta[recordKey] = this.fDBObj.GetObjectRecordID();
      }
      if (isPositionDocument === this.fDBObj.IsPositionObj()) {
        if (this.fDelta[parentRecordKey] == null) {
          this.fDelta[parentRecordKey] = this.fDBObj.GetObjectSupervisorID();
        }
      } else {
        this.fDelta[this.fDBObj.GetFieldDefByID(kEmployeePositionID).GetFieldName()] =
          this.fDBObj.SmartGetFieldValue(kEmployeePositionID);
      }
    }
    Object.keys(incomingChangeDelta).forEach(key => {
      this.fDelta[key] = incomingChangeDelta[key];
    });
    if (newType) this.fChangeType = newType;
    if (changeDict["author"]) this.fChangeAuthor = changeDict["author"];
    if (changeDict["date"]) this.fChangeDate = changeDict["date"];
  }

  Clone(override) {
    var c = new QChange();
    c.fPlanMgr = this.fPlanMgr;
    c.fWSMgr = this.fWSMgr;
    c.fDBObj = this.fDBObj;
    c.fParentOverride = override;
    c.fChangeType = this.fChangeType;
    c.fChangeAuthor = this.fChangeAuthor;
    c.fChangeDate = this.fChangeDate;
    c.fActionID = this.fActionID;
    c.fDelta = JSON.parse(JSON.stringify(this.fDelta));
    c.fConflict = this.fConflict;
    c.fConflictFields = this.fConflictFields;
    c.fAuthorBlockedFields = this.fAuthorBlockedFields ? [...this.fAuthorBlockedFields] : null;
    c.fIsReverted = this.fIsReverted;
    c.fEffectiveDate = this.fEffectiveDate;
    c.fOldRecVals = this.fOldRecVals;
    return c;
  }

  Serialize() {
    var serializedResult = {};
    serializedResult["type"] = this.fChangeType;
    if (this.fChangeAuthor) serializedResult["author"] = this.fChangeAuthor;
    if (this.fChangeDate) serializedResult["date"] = this.fChangeDate;
    serializedResult["data"] = this.fDelta;
    serializedResult["conflict"] = this.fConflict;
    serializedResult["revert"] = this.fIsReverted;
    serializedResult["actionID"] = this.fActionID;
    if (this.fEffectiveDate) {
      // save date as utc time hour 12
      var utcDate = this.fEffectiveDate.convertToUTCNoonDate();
      var effDate = utcDate.FormatDate(23);
      serializedResult["effectiveDate"] = effDate;
    }
    if (this.fAuthorBlockedFields) {
      serializedResult["authorBlockedFields"] = [...this.fAuthorBlockedFields];
    }

    return serializedResult;
  }

  GetChangeAuthor() {
    return this.fChangeAuthor;
  }

  GetChangeDate() {
    var changeDate = this.fChangeDate;
    return changeDate;
  }

  GetChangeEffectiveDate() {
    if (this.fEffectiveDate) {
      return this.fEffectiveDate;
    }
    var planMgr = this.fPlanMgr;
    var createDate = planMgr.fPlanInfo["createDate"];
    var dateObj = convertStdDateString(createDate);
    return dateObj;
  }

  GetSortableDate() {
    if (this.fChangeDate) {
      return convertStdDateString(this.fChangeDate);
    }

    const planMgr = this.fPlanMgr;
    const createDate = planMgr.fPlanInfo["createDate"];
    return convertStdDateString(createDate);
  }

  GetFormattedChangeDateTime() {
    var timePref = null;
    if (this.fWSMgr) {
      var oi = this.fWSMgr.GetOrgInfo();
      timePref = oi.GetTimeFormat();
    }
    var changeDate = this.fChangeDate;
    if (changeDate) {
      var d = new Date();
      changeDate = d.convertUTCDateToLocalDateString(
        changeDate,
        null,
        timePref,
        enumDateTimeDisplayOptions.todayyesterday
      );
    }
    return changeDate;
  }

  GetFormattedChangeTime() {
    var timePref = null;
    if (this.fWSMgr) {
      var oi = this.fWSMgr.GetOrgInfo();
      timePref = oi.GetTimeFormat();
    }
    var changeDate = this.fChangeDate;
    if (changeDate) {
      var d = new Date();
      changeDate = d.convertUTCDateToLocalDateString(
        changeDate,
        null,
        timePref,
        enumDateTimeDisplayOptions.time
      );
    }
    return changeDate;
  }

  IsConflicted() {
    return this.fConflict;
  }

  IsAuthorBlockedField(fieldName) {
    if (!this.fAuthorBlockedFields) return false;
    return this.fAuthorBlockedFields.includes(fieldName);
  }

  IsReverted() {
    return this.fIsReverted;
  }

  IsDateEffective() {
    //Suppressing the logic until we have a timeline view.
    return true;
    var planMgr = this.fPlanMgr;
    if (!planMgr.GetUseEffectiveDates()) return true;

    var curDate = this.fEffectiveDate;
    if (!curDate) return true;

    if (new Date() > curDate) return true;

    return false;
  }

  GetChangeType() {
    return this.fChangeType;
  }

  GetChangeHint() {
    var o = this.fDBObj;
    var v = o.FormatEmployeeLabel();
    return v;
  }

  SetActionID(actionID) {
    this.fActionID = actionID;
  }

  GetActionID() {
    return this.fActionID;
  }

  GetConflictHint() {
    var oldRectVals = this.fOldRecVals;
    var theLabel = FormatEmployeeDictLabel(oldRectVals);
    return theLabel;
  }

  GetChangePersonID() {
    var o = this.fDBObj;
    var pID = o.GetObjectRecordID("PersonID");
    return pID;
  }

  GetAuthorTimeStamp() {
    var planInfo = this.fPlanMgr.GetPlanInfo();
    var usersList = planInfo["users"];
    var changeAuthor = this.GetChangeAuthor();
    for (var u in usersList) {
      var user = usersList[u];
      if (changeAuthor == user.name && user.displayName != changeAuthor) {
        changeAuthor = user.displayName;
        break;
      }
    }

    var changeTime = this.GetFormattedChangeTime();

    return changeTime + " " + appstr("by") + " " + changeAuthor;
  }

  GetChangeName() {
    var o = this.fDBObj;
    if (o == null) return appstr("Blank");
    var v = o.SmartGetFieldValue("Name");
    if (isBlockedField(v)) return getBlockedFieldDisplayValue();
    if (v == "" || v == null) v = "<Blank>";

    return v;
  }

  GetChangeValue(fieldID) {
    var o = this.fDBObj;
    if (o == null) return appstr("Blank");
    var v = o.SmartGetFieldValue(fieldID);
    if (isBlockedField(v)) return getBlockedFieldDisplayValue();
    if (v == "" || v == null) v = "<Blank>";

    return v;
  }

  GetOriginalValue(fieldDef) {
    var o = this.fDBObj;
    if (!o) return "<Removed>";
    if (o.fCloneBase) o = this.fDBObj.fCloneBase;
    var fieldValue = o.GetFieldValue(fieldDef, false);
    if (isBlockedField(fieldValue)) return getBlockedFieldDisplayValue();
    if (fieldValue === "" || fieldValue == null) fieldValue = "<Blank>";

    return fieldValue;
  }

  GetDelta() {
    return this.fDelta;
  }

  GetOverride() {
    return this.fParentOverride;
  }

  GetChangeComment() {
    var o = this.fParentOverride;
    var v1 = o.SmartGetFieldValue("Comment");
    if (isBlockedField(v1)) return getBlockedFieldDisplayValue();
    if (v1 == "" || v1 == null) return appstr("No comments");

    return v1;
  }

  /**
   *
   * @param {QObjDBFieldDef} fieldDef
   * @returns
   */
  GetCurrentValue(fieldDef) {
    let value = null;

    const parentClass = this.fDBObj?.GetParentClass();
    const fieldName = fieldDef.GetFieldName();

    if (fieldName) {
      value = this.fDBObj?.GetFieldValue(fieldDef);
    }

    if (fieldName && this.fDelta[fieldName] != null) {
      value = this.fDelta[fieldName];
    }

    return value;
  }

  GetSupervisorID(getParentPositionIfHolder = false) {
    let supervisorID = null;

    const parentClass = this.fDBObj?.GetParentClass();
    const supervisorIDField = getParentPositionIfHolder
      ? parentClass?.GetParentIDFieldDef()
      : parentClass?.GetSupervisorIDFieldDef();
    const supervisorFieldName = supervisorIDField?.GetFieldName();

    if (supervisorIDField) {
      supervisorID = this.fDBObj?.GetFieldValue(supervisorIDField);
    }

    if (supervisorFieldName && this.fDelta[supervisorFieldName] != null) {
      supervisorID = this.fDelta[supervisorFieldName];
    }

    return supervisorID;
  }

  GetSupervisorLabel() {
    let doc = Globals._wsMgr.GetDoc();
    let supervisorID = this.GetSupervisorID();
    let label = doc.GetEmployeeLabelByPersonID(supervisorID);
    if (label === appstr("refinvalid") && supervisorID != null) label = supervisorID;
    return label;
  }

  /**
   * This method will return a list of field changes for the change
   *
   * @returns {Array<{ fieldDef: QObjDBFieldDef, value: string, displayValue: string }>}
   */
  GetDisplayRows() {
    const delta = this.GetDelta();
    const override = this.fParentOverride;
    // The fallback is a temporary fix to until OCN5-4691 is resolved
    const objType = override.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;

    const parentClass = override.GetParentClass();
    const objDb = parentClass.GetObjDB();
    const objClassName = objTypeClassMap[objType];
    const objClass = objDb.GetClassByName(objClassName);
    const recordIDField = objClass.GetRecordIDFieldDef();

    const blankValue = `<${appstr("Blank")}>`;

    const rows = [];

    for (const [fieldName, value] of Object.entries(delta)) {
      if (typeof value === "object" && value !== null) {
        continue;
      }

      const fieldDef = objClass.GetFieldDefByName(fieldName);
      if (!fieldDef) {
        // This is an issue - a changed field was not found
        continue;
      }

      if (fieldDef === recordIDField) {
        // Skip the record ID field
        continue;
      }

      const formattedValue = FormatFieldValue(value, fieldDef);
      const displayValue = formattedValue || formattedValue === null ? formattedValue : blankValue;

      rows.push({
        fieldDef,
        value,
        displayValue
      });
    }

    const priorityFields = [];

    const parentFieldDef = objClass.GetParentIDFieldDef();
    const parentFieldName = parentFieldDef?.GetFieldName();
    if (parentFieldName) {
      priorityFields.push(parentFieldName);
    }

    rows.sort((a, b) => {
      const aLabel = a.fieldDef.GetFieldName();
      const bLabel = b.fieldDef.GetFieldName();

      const aIsPriority = priorityFields.includes(aLabel);
      const bIsPriority = priorityFields.includes(bLabel);

      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;

      const aIsUnderscore = aLabel.startsWith("_");
      const bIsUnderscore = bLabel.startsWith("_");

      if (aIsUnderscore && !bIsUnderscore) return 1;
      if (!aIsUnderscore && bIsUnderscore) return -1;

      return aLabel.localeCompare(bLabel);
    });

    return rows;
  }

  GetChangeEmployee() {
    let dataObj = this.fDBObj;
    // fParentOverride can be null for standalone changes (e.g., displayObject-only path)
    let displayObj = this.fParentOverride?.SmartGetFieldValue("displayObject");
    let recObj = dataObj ?? displayObj;
    if (recObj) {
      // Dict path: displayObject from the server is a plain object, not a QObjDBObject.
      if (!(recObj instanceof QObjDBObject)) {
        const flatDelta = GetFlattenedChanges(this.fParentOverride);
        if (Object.keys(flatDelta).length === 0) {
          return FormatEmployeeDictLabel(recObj);
        }
        const merged = { ...recObj };
        for (const [key, value] of Object.entries(flatDelta)) {
          if (value != null) {
            merged[key] = sanitizeBlockedValue(value);
          }
        }
        return FormatEmployeeDictLabel(merged);
      }

      const parentClass = recObj.GetParentClass();
      // Build flattened plan change delta so the label reflects the current
      // plan state instead of stale base/CRLS-filtered values.
      const override = this.fParentOverride;
      const flatDelta = GetFlattenedChanges(override);

      if (Object.keys(flatDelta).length === 0) {
        return recObj.FormatEmployeeLabel();
      }

      // Rebuild the label with plan values overlaid on recObj values.
      // Mirrors QObjDBObject.FormatEmployeeLabel but substitutes changed fields.
      const isPositionObj = recObj.IsPositionObj();
      const fieldIdList = isPositionObj
        ? Globals._uiConfig.fPositionLabelFields
        : Globals._uiConfig.fEmployeelabelFields;
      let formattedString = "";
      const n = fieldIdList?.length ?? 0;
      for (let i = 0; i < n; i++) {
        let theField = fieldIdList[i];
        if (isPositionObj && theField === kPersonID) theField = kPositionID;
        if (isPositionObj && theField === kTitleID) theField = kPositionTitleID;
        let fieldValue = recObj.SmartGetFieldValue(theField);
        const fieldDef = parentClass.GetFieldDefByID(theField);
        const fieldName = fieldDef?.GetFieldName();
        if (fieldName && flatDelta[fieldName] != null) {
          fieldValue = sanitizeBlockedValue(flatDelta[fieldName]);
        }
        formattedString = FormatEmployeeLabelValue(formattedString, fieldValue, i, i === n - 1);
      }
      return formattedString;
    }

    return appstr("Blank");
  }

  GetHeader() {
    let theOverride = this.fParentOverride;
    let changeType = theOverride.SmartGetFieldValue("Type");
    let PersonSupplementText = "";
    let numRows = 1;
    let fieldList = [];
    var fields = this.fDelta;
    if (changeType == enumOverrideType.change) {
      for (var field in fields) {
        fieldList.push(field);
      }
      ///Title, Location, and X change
      if (fieldList.length === 1) {
        PersonSupplementText = fieldList[0];
        // Add rows for the fields
        numRows += 1;
      } else if (fieldList.length < 4) {
        PersonSupplementText = formatArray(fieldList);
      } else {
        PersonSupplementText = fieldList.length.toString();
      }
    }
    return PersonSupplementText;
  }
}
